import { useEffect, useRef, useState } from 'react'
import { useIsAgentCreated } from '@/app/providers/AgentCreatedContext'

const VISIBLE_MS = 6000

/**
 * Renders a shimmer overlay INSIDE the parent element.
 * Place as first child of the card/row's real DOM root.
 *
 * Also adds `agent-shimmer-active` class to the parent element so that
 * border glow, shadow, and pulse effects can radiate OUTSIDE the card
 * boundary (not clipped by overflow:hidden).
 */
export function AgentShimmer({
  objectId,
}: {
  objectId: string | number | null | undefined
}) {
  const active = useIsAgentCreated(objectId)
  const [visible, setVisible] = useState(false)
  const startedAtRef = useRef(0)
  const overlayRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!active) return
    const startedAt = Date.now()
    startedAtRef.current = startedAt
    setVisible(true)

    // Add class to parent so glow/shadow bleeds outside the card
    const parent = overlayRef.current?.parentElement
    if (parent) parent.classList.add('agent-shimmer-active')

    const timer = window.setTimeout(() => {
      if (startedAtRef.current === startedAt) {
        setVisible(false)
        if (parent) parent.classList.remove('agent-shimmer-active')
      }
    }, VISIBLE_MS)

    return () => {
      window.clearTimeout(timer)
      if (parent) parent.classList.remove('agent-shimmer-active')
    }
  }, [active])

  if (!visible) return null

  return (
    <span ref={overlayRef} aria-hidden className="agent-shimmer-overlay">
      <span className="agent-shimmer-wash" />
    </span>
  )
}
